/**
 * All schedule times are stored as `event_day` + minutes from midnight
 * (SPEC §7): DST-safe. Rendering to
 * wall-clock strings happens here; date rendering uses dayjs in the apps.
 */

export const MINUTES_PER_DAY = 24 * 60;

export function hmToMinutes(hm: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hm.trim());
  if (!m) throw new Error(`Invalid time "${hm}", expected H:MM or HH:MM`);
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (hours > 24 || minutes > 59 || (hours === 24 && minutes > 0)) {
    throw new Error(`Invalid time "${hm}", out of range`);
  }
  return hours * 60 + minutes;
}

/** 390 → "06:30" */
export function minutesToHm(total: number): string {
  if (!Number.isInteger(total) || total < 0 || total > MINUTES_PER_DAY) {
    throw new Error(`Invalid minutes value ${total}`);
  }
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** 390 → "6:30" — used for meeting times. */
export function minutesToHmShort(total: number): string {
  const hm = minutesToHm(total);
  return hm.startsWith('0') ? hm.slice(1) : hm;
}

/** Duration: 240 → "4 h", 270 → "4,5 h", 100 → "1 h 40 min" */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (rest === 0) return `${h} h`;
  if (rest === 30) return `${h},5 h`;
  return `${h} h ${rest} min`;
}

export interface MinuteRange {
  fromMin: number;
  toMin: number;
}

export function rangesOverlap(a: MinuteRange, b: MinuteRange): boolean {
  return a.fromMin < b.toMin && b.fromMin < a.toMin;
}

/** Gap in minutes between two non-overlapping ranges; 0 if they touch or overlap. */
export function gapBetween(a: MinuteRange, b: MinuteRange): number {
  const [first, second] = a.fromMin <= b.fromMin ? [a, b] : [b, a];
  return Math.max(0, second.fromMin - first.toMin);
}

/**
 * Smallest range covering every input range, or null when there are none.
 *
 * The min-of-starts / max-of-ends pair is asked for wherever a set of shifts or
 * windows has to be shown or measured as one span: a post's day extent when it
 * declares no operating window, a day's extent when the board sizes its axis.
 */
export function dayExtent(ranges: MinuteRange[]): MinuteRange | null {
  if (!ranges.length) return null;
  let fromMin = Infinity;
  let toMin = -Infinity;
  for (const r of ranges) {
    if (r.fromMin < fromMin) fromMin = r.fromMin;
    if (r.toMin > toMin) toMin = r.toMin;
  }
  return { fromMin, toMin };
}
